import { z } from 'zod';
import { baseModelSchema } from '../../../model/baseModel';
import { IReportFormValues } from './reportFormValues';

/**
 * Zod schema for validating report data.
 * Extends the base model schema with report-specific fields.
 *
 * @property {string} name - Name of the report (max 255 characters)
 * @property {boolean} canLivePalletize - Whether the report supports live palletization
 * @property {number} printServerId - ID of the print server associated with this report
 */
export const reportSchema = baseModelSchema.extend({
    name: z.string().nonempty('Name is required').max(255, 'Name can only be up to 255 characters long.').default(''),
    canLivePalletize: z.boolean().default(true),
    printServerId: z.number(),
});

/**
 * TypeScript interface representing a Report object.
 * Generated from the Zod schema for type safety.
 */
export type IReport = z.infer<typeof reportSchema>;

/**
 * Creates a validated Report object from form values.
 *
 * @param {IReportFormValues} formValues - The form data to convert
 * @returns {IReport} A validated report object
 * @throws {ZodError} If validation fails
 */
export function Report(formValues : IReportFormValues) : IReport {
    const { printServer } = formValues;
    return reportSchema.parse({
        ...formValues,
        printServerId: printServer?.value ? Number(printServer.value) : undefined,
    });
};
